import { useState } from "react";
import { Flex, Spinner, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  IEnvioResultado,
  IHistoricoNotificacaoCcbItem,
  IHistoricoNotificacoesCcbRetorno,
} from "@/models/consignado/proposal-billings.model";
import {
  buscarHistoricoNotificacoesPorCcb,
  reenviarNotificacao,
} from "@/services/consignado/proposta-cobranca.service";
import CollapsibleSection from "./CollapsibleSection";
import HistoricoNotificacoesTable from "./HistoricoNotificacoesTable";

type Props = {
  numeroCcb: string;
};

// Seção "Histórico de Notificações" do detalhe da CCB — REAL (TAR-050,
// backend his-adm-cobranca-020 `72c3b158`): GET /proposta/cobrancas/:ccb/
// historico-notificacoes. Nasce colapsada e só dispara a query na 1ª abertura
// (mesmo lazy-load das outras seções do CcbDetalhe).
//
// Reenvio: POST /notificacoes/:id/reenviar. 200 = tudo enviado; 207 = parcial
// (resolve, algum canal com FALHA); 502 = tudo falhou (o axios lança; o body é
// a mesma lista de resultados, não `{ message }`). Depois de qualquer reenvio
// a query é invalidada — o backend grava uma notificação nova, que entra no
// histórico.
export default function HistoricoNotificacoesSection({ numeroCcb }: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [aberta, setAberta] = useState(false);

  const { data, isLoading, isError } = useQuery<IHistoricoNotificacoesCcbRetorno>({
    queryKey: ["historico-notificacoes-ccb", numeroCcb],
    queryFn: () => buscarHistoricoNotificacoesPorCcb(numeroCcb),
    enabled: aberta,
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const invalidar = () =>
    queryClient.invalidateQueries({ queryKey: ["historico-notificacoes-ccb", numeroCcb] });

  const reenviar = useMutation({
    mutationFn: (item: IHistoricoNotificacaoCcbItem) => reenviarNotificacao(item.id),
    onSuccess(resultados) {
      const falhas = resultados.filter((r) => r.status === "FALHA");
      if (falhas.length === 0) {
        toast({
          title: "Notificação reenviada",
          description: descreverCanais(resultados),
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Reenvio parcial",
          description: descreverFalhas(falhas),
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      }
      invalidar();
    },
    onError(error: AxiosError<IEnvioResultado[] | { message?: string }>) {
      const body = error.response?.data;
      toast({
        title: "Erro ao reenviar notificação",
        description: Array.isArray(body)
          ? descreverFalhas(body)
          : body?.message || "Erro inesperado.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      invalidar();
    },
  });

  const conteudo = isLoading ? (
    <Flex justifyContent="center" py={4}>
      <Spinner color="primary" />
    </Flex>
  ) : isError || !data ? (
    <Text fontSize="sm" color="gray.500">
      Não foi possível carregar o histórico de notificações desta CCB.
    </Text>
  ) : (
    <HistoricoNotificacoesTable
      notificacoes={data.notificacoes}
      onReenviar={(item) => reenviar.mutateAsync(item)}
    />
  );

  return (
    <CollapsibleSection
      title="Histórico de Notificações"
      defaultExpanded={false}
      onToggle={(expanded) => expanded && setAberta(true)}
    >
      {aberta && conteudo}
    </CollapsibleSection>
  );
}

const descreverCanais = (resultados: IEnvioResultado[]) =>
  resultados.length ? `Enviado por ${resultados.map((r) => r.canal).join(", ")}.` : undefined;

const descreverFalhas = (falhas: IEnvioResultado[]) =>
  falhas.map((f) => `${f.canal}: ${f.erro || "falha no envio"}`).join(" · ");
